import { useCallback, useEffect, useRef, useState } from 'react';

import { env } from '../lib/env.js';
import { SAMPLE_RATE, floatTo16BitPCM, int16ToBase64 } from '../lib/audio/pcm.js';
import { VoicePlayer } from '../lib/audio/player.js';

// status: idle | connecting | live | error | closed
// The orb shows finer states (listening/speaking) from `speaking`+`userSpeaking`.

function buildWsUrl(params) {
  const base = env.apiBase || '/api/v1';
  const httpBase = /^https?:\/\//.test(base) ? base : window.location.origin + base;
  const url = new URL(`${httpBase}/voice/session`);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v) url.searchParams.set(k, v);
  });
  return url.toString();
}

/**
 * Realtime voice session against the server broker. Handles microphone
 * capture (PCM16 @ 24 kHz), audio playback, live transcripts, and status.
 */
export function useRealtimeVoice() {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(false);
  const [speaking, setSpeaking] = useState(false);       // assistant audio playing
  const [userSpeaking, setUserSpeaking] = useState(false); // server VAD detected speech
  const [transcript, setTranscript] = useState([]);       // [{ id, role, text, final }]
  const [meta, setMeta] = useState(null);                 // broker.ready payload

  const wsRef = useRef(null);
  const ctxRef = useRef(null);
  const streamRef = useRef(null);
  const nodeRef = useRef(null);
  const playerRef = useRef(null);
  const mutedRef = useRef(false);
  const asstIdRef = useRef(null);

  const appendAssistantDelta = useCallback((delta) => {
    setTranscript((prev) => prev.map((m) => (
      m.id === asstIdRef.current ? { ...m, text: m.text + delta } : m
    )));
  }, []);

  const finalizeAssistant = useCallback((fullText) => {
    setTranscript((prev) => prev.map((m) => (
      m.id === asstIdRef.current
        ? { ...m, text: typeof fullText === 'string' && fullText ? fullText : m.text, final: true }
        : m
    )));
  }, []);

  const cleanup = useCallback(() => {
    if (nodeRef.current) { try { nodeRef.current.disconnect(); } catch (_e) { /* */ } nodeRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (ctxRef.current) { try { ctxRef.current.close(); } catch (_e) { /* */ } ctxRef.current = null; }
    if (playerRef.current) { playerRef.current.close(); playerRef.current = null; }
    if (wsRef.current) { try { wsRef.current.close(); } catch (_e) { /* */ } wsRef.current = null; }
  }, []);

  const stop = useCallback(() => {
    cleanup();
    setStatus('closed');
    setSpeaking(false);
    setUserSpeaking(false);
  }, [cleanup]);

  const handleEvent = useCallback((evt) => {
    switch (evt.type) {
      case 'broker.ready':
        setMeta(evt);
        setStatus('live');
        break;
      case 'broker.error':
      case 'error': {
        // Broker errors carry `message`; OpenAI Realtime `error` events carry
        // the detail in `error.message` (and `error.code`).
        const detail = evt.message
          || (evt.error && (evt.error.message || evt.error.code))
          || 'Errore di connessione vocale';
        setError(detail);
        setStatus('error');
        break;
      }
      case 'broker.notice':
        // idle/budget notices: surface as soft error then the socket closes.
        setError(evt.message || null);
        break;
      case 'input_audio_buffer.speech_started':
        setUserSpeaking(true);
        // Barge-in: stop assistant playback when the user starts talking.
        if (playerRef.current) playerRef.current.clear();
        break;
      case 'input_audio_buffer.speech_stopped':
        setUserSpeaking(false);
        break;
      case 'conversation.item.input_audio_transcription.completed':
        if (evt.transcript) {
          setTranscript((prev) => {
            const userMsg = { id: `u-${prev.length}-${Date.now()}`, role: 'user', text: evt.transcript, final: true };
            // The user's speech precedes the assistant reply, but its
            // transcription often completes after the assistant bubble has
            // already opened. Insert it before a trailing in-progress
            // assistant bubble so the turn order stays natural.
            const last = prev.length - 1;
            if (last >= 0 && prev[last].role === 'assistant' && !prev[last].final) {
              const copy = [...prev];
              copy.splice(last, 0, userMsg);
              return copy;
            }
            return [...prev, userMsg];
          });
        }
        break;
      case 'response.created':
        // Open a new assistant bubble to receive transcript deltas.
        asstIdRef.current = `a-${Date.now()}`;
        setTranscript((prev) => [...prev, { id: asstIdRef.current, role: 'assistant', text: '', final: false }]);
        break;
      case 'response.output_audio_transcript.delta':
      case 'response.audio_transcript.delta':
        if (evt.delta) appendAssistantDelta(evt.delta);
        break;
      case 'response.output_audio_transcript.done':
      case 'response.audio_transcript.done':
        finalizeAssistant(evt.transcript);
        break;
      case 'response.output_audio.delta':
      case 'response.audio.delta':
        if (evt.delta && playerRef.current) playerRef.current.enqueue(evt.delta);
        break;
      default:
        break;
    }
  }, [appendAssistantDelta, finalizeAssistant]);

  const start = useCallback(async (params) => {
    setError(null);
    setTranscript([]);
    setStatus('connecting');
    mutedRef.current = false;
    setMuted(false);

    try {
      // 1. Microphone + 24 kHz capture graph.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true }
      });
      streamRef.current = stream;

      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx({ sampleRate: SAMPLE_RATE });
      ctxRef.current = ctx;
      await ctx.audioWorklet.addModule('/worklets/recorder-worklet.js');

      const player = new VoicePlayer(setSpeaking);
      playerRef.current = player;

      // 2. WebSocket to the broker (cookies sent automatically, same origin).
      const ws = new WebSocket(buildWsUrl(params));
      wsRef.current = ws;

      const source = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, 'recorder-processor');
      nodeRef.current = node;
      node.port.onmessage = (e) => {
        if (mutedRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        const pcm = floatTo16BitPCM(e.data);
        wsRef.current.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: int16ToBase64(pcm) }));
      };
      source.connect(node);
      // Keep the node pulling without routing mic to speakers.
      node.connect(ctx.destination);

      ws.onmessage = (e) => {
        let evt;
        try { evt = JSON.parse(e.data); } catch (_err) { return; }
        handleEvent(evt);
      };
      ws.onerror = () => { setError('Connessione vocale fallita'); setStatus('error'); };
      ws.onclose = () => { setStatus((s) => (s === 'error' ? s : 'closed')); };
    } catch (err) {
      setError(err && err.name === 'NotAllowedError' ? 'Permesso microfono negato' : (err.message || 'Avvio voce fallito'));
      setStatus('error');
      cleanup();
    }
  }, [handleEvent, cleanup]);

  const toggleMute = useCallback(() => {
    mutedRef.current = !mutedRef.current;
    setMuted(mutedRef.current);
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  return { status, error, transcript, meta, speaking, userSpeaking, muted, start, stop, toggleMute };
}
